/**
 * Shared form-level validators for app create / settings forms.
 * Kept here so the create drawer and the settings tab agree on what
 * "valid" means for URL-shaped fields.
 */

import type { Rule } from 'antd/es/form'

/**
 * Accepts an empty string (the field is optional) OR a value whose protocol
 * is http or https. Any other shape (mailto:, ftp:, missing protocol, …)
 * is rejected.
 *
 * AntD's built-in `type: 'url'` rule accepts other schemes too (ftp, etc.)
 * and rejects scheme-relative URLs, which surprises users. Whitelisting
 * http/https here makes the intent obvious and the failure mode predictable.
 */
export const buildHttpUrlRule = (invalidMessage: string): Rule => ({
  validator: (_rule, raw: string | undefined): Promise<void> => {
    const value = (raw ?? '').trim()
    if (!value) return Promise.resolve()
    try {
      const url = new URL(value)
      if (url.protocol === 'http:' || url.protocol === 'https:') {
        return Promise.resolve()
      }
    } catch {
      // fall through
    }

    return Promise.reject(new Error(invalidMessage))
  }
})
